import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CircleNotch } from "@phosphor-icons/react";
import { AuthShell, inputCls, inputStyle } from "./Login";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [valid, setValid] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setValid(false);
      return;
    }
    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then((r) => setValid(!!r.data.valid))
      .catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. You can now log in.");
      navigate("/login?reset=success");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Set a new password" subtitle="Pick something strong — Rowan will keep the books, you keep the keys.">
      {valid === null && (
        <div className="flex items-center gap-2 text-sm" data-testid="reset-checking" style={{ color: "var(--bk-text-2)" }}>
          <CircleNotch size={18} className="animate-spin" /> Checking your reset link…
        </div>
      )}
      {valid === false && (
        <div data-testid="reset-invalid">
          <div className="text-sm px-4 py-3 rounded-xl" style={{ background: "#FDECE8", color: "#B23B1F" }}>
            That reset link is invalid or has expired. Request a new one from the login page.
          </div>
          <p className="text-sm mt-6" style={{ color: "var(--bk-text-2)" }}>
            <Link to="/login" className="font-semibold" style={{ color: "var(--bk-accent)" }} data-testid="reset-back-to-login">Back to log in</Link>
          </p>
        </div>
      )}
      {valid === true && (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
          {error && (
            <div data-testid="reset-error" className="text-sm px-4 py-3 rounded-xl" style={{ background: "#FDECE8", color: "#B23B1F" }}>
              {error}
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>New password</label>
            <input data-testid="reset-new-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls + " mt-1.5"} style={inputStyle} placeholder="At least 8 characters" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Confirm password</label>
            <input data-testid="reset-confirm-password" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls + " mt-1.5"} style={inputStyle} placeholder="Type it again" />
          </div>
          <button data-testid="reset-submit" type="submit" disabled={loading} className="bk-btn-primary w-full py-3 text-sm font-semibold disabled:opacity-60">
            {loading ? "Updating…" : "Set new password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
